import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ListChecks, X } from 'lucide-react';

import { IMPRESSION_SUIVI_LOTS, lotsAPI } from '../../lib/lotsApi';
import {
  ETAPES_LOT,
  ETATS_LIGNE,
  RETOURS_LOT,
  STATUTS_LOT,
  avecFiltresSuivi,
  lireFiltresSuivi,
  nombreFiltresSuivi,
  parametresSuivi,
  sansFiltresSuivi,
} from '../../lib/lotsFiltres';
import { libelle } from '../../lib/libelles';
import { messageErreur } from '../../lib/erreurs';
import { formatVolume } from '../../lib/utils';
import { toast } from '../../components/ui/sonner';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { usePaginationServeur } from '../../hooks/usePagination';
import { useDerniereDemande } from '../../hooks/useDerniereDemande';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { useDroitsLots } from '../../hooks/useDroitsLots';
import { useTransporteursLots } from '../../hooks/useTransporteursLots';
import { PageHeader } from '../../components/ui/page-header';
import { PrintMenu } from '../../components/business/PrintMenu';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BarrePagination } from '../../components/ui/pagination-bar';
import { EmptyState } from '../../components/ui/empty-state';
import { LoadingState } from '../../components/ui/loading-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  BoutonEnvoiResponsable,
  CamionLigne,
  ChampRecherche,
  ChauffeurLigne,
  DatesLigne,
  EnteteDates,
  EtapeRetourLigne,
  LienLot,
  LienTrajet,
} from '../../components/lots/lotsUi';
import { produitsDuLot } from '../../lib/lotsProduits';

const TOUS = 'tous';

/** Menu de choix simple d'un filtre à codes : « tous » ou un code. */
const FiltreCodes = ({ valeur, codes, domaine, titre, surChoix, largeur = 'w-40', testid }) => (
  <Select value={valeur || TOUS} onValueChange={(choix) => surChoix(choix === TOUS ? '' : choix)}>
    <SelectTrigger className={`input-base ${largeur}`} aria-label={titre} data-testid={testid}>
      <SelectValue placeholder={titre} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value={TOUS}>{titre} : tous</SelectItem>
      {codes.map((code) => (
        <SelectItem key={code} value={code}>
          {libelle(domaine, code)}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

/** Suivi : les camions de tous les lots, filtrables, imprimables, envoyés au responsable. */
const LotsSuiviPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { peutEcrire } = useDroitsLots();
  const { options: societes } = useTransporteursLots();

  // Les filtres vivent dans l'adresse : un lien partagé rouvre la même liste.
  const filtres = useMemo(() => lireFiltresSuivi(searchParams), [searchParams]);
  const cleFiltres = JSON.stringify(parametresSuivi(filtres));
  const parametres = useMemo(() => JSON.parse(cleFiltres), [cleFiltres]);
  const filtresActifs = nombreFiltresSuivi(filtres);

  const [recherche, setRecherche] = useState(filtres.q);
  const rechercheDifferee = useDebouncedValue(recherche);
  const [lignes, setLignes] = useState([]);
  const [total, setTotal] = useState(0);
  const [lots, setLots] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);

  const changer = useCallback(
    (changements) => setSearchParams((precedent) => avecFiltresSuivi(precedent, changements), { replace: true }),
    [setSearchParams],
  );

  useEffect(() => {
    if (rechercheDifferee.trim() === filtres.q) return;
    changer({ q: rechercheDifferee.trim() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rechercheDifferee]);

  // Pagination commune : page et taille dans l'adresse ; un autre filtre ramène à la première page.
  const pagination = usePaginationServeur(total, { reinitialiser: cleFiltres });
  const { page, limit: taille } = pagination;

  const nouvelleDemande = useDerniereDemande();
  const charger = useCallback(
    async ({ silencieux = false } = {}) => {
      const perimee = nouvelleDemande();
      if (!silencieux) setChargement(true);
      try {
        const resultat = await lotsAPI.trucks({ ...parametres, page, page_size: taille });
        if (perimee()) return;
        setLignes(resultat.items);
        setTotal(resultat.total);
        setErreur(false);
      } catch (err) {
        if (perimee()) return;
        console.error('Failed to load lot trucks:', err);
        setErreur(true);
        toast.error(messageErreur(err, 'Impossible de charger le suivi des camions'));
      } finally {
        if (!perimee()) setChargement(false);
      }
    },
    [parametres, page, taille, nouvelleDemande],
  );

  useEffect(() => {
    charger();
  }, [charger]);

  // Le menu des lots : tous, jamais seulement la première page.
  const chargerLots = useCallback(async () => {
    try {
      setLots(await lotsAPI.list());
    } catch (err) {
      setLots([]);
    }
  }, []);

  useEffect(() => {
    chargerLots();
  }, [chargerLots]);

  useRafraichirSur(['lots', 'trajets'], (options) => Promise.all([charger(options), chargerLots()]));

  const optionsLots = useMemo(
    () => [
      { value: '', label: 'Tous les lots' },
      ...lots.map((lot) => ({
        value: lot.id,
        label: lot.reference,
        description: lot.transporter_name || undefined,
        keywords: [lot.transporter_name, produitsDuLot(lot)],
      })),
    ],
    [lots],
  );
  const optionsTransporteurs = useMemo(
    () => [{ value: '', label: 'Tous les transporteurs' }, ...societes],
    [societes],
  );

  const effacer = () => {
    setRecherche('');
    setSearchParams((precedent) => sansFiltresSuivi(precedent), { replace: true });
  };

  // Le document imprimé dit ce que l'écran affiche : il reçoit exactement les
  // filtres de la liste, jamais la pagination.
  const elementsImpression = [
    {
      label: 'Suivi des camions',
      filename: 'suivi-des-lots',
      url: IMPRESSION_SUIVI_LOTS,
      formats: ['pdf', 'xlsx', 'csv'],
      params: parametres,
    },
  ];

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="lots-suivi">
      <PageHeader
        eyebrow="Lots attribués"
        title="Suivi"
        stats={[{ label: filtresActifs ? 'Camions trouvés' : 'Camions', valeur: total, testid: 'suivi-total' }]}
        actions={
          <>
            <PrintMenu items={elementsImpression} label="Imprimer" />
            {peutEcrire ? <BoutonEnvoiResponsable params={parametres} className="!max-w-none" testid="suivi-send-manager" /> : null}
          </>
        }
      />

      {/* Recherche étroite, filtres à sa droite. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="suivi-filtres">
        <ChampRecherche
          valeur={recherche}
          onChange={setRecherche}
          placeholder="Plaque, chauffeur, N° de bon…"
          label="Rechercher un camion"
          testid="suivi-recherche"
        />
        <SearchableSelect
          className="input-base w-44"
          data-testid="suivi-filtre-lot"
          aria-label="Lot"
          placeholder="Lot"
          searchPlaceholder="Rechercher un lot…"
          emptyMessage="Aucun lot trouvé"
          value={filtres.lot}
          onValueChange={(valeur) => changer({ lot: valeur })}
          options={optionsLots}
        />
        <SearchableSelect
          className="input-base w-48"
          data-testid="suivi-filtre-transporteur"
          aria-label="Transporteur"
          placeholder="Transporteur"
          searchPlaceholder="Rechercher un transporteur…"
          emptyMessage="Aucun transporteur trouvé"
          value={filtres.transporteur}
          onValueChange={(valeur) => changer({ transporteur: valeur })}
          options={optionsTransporteurs}
        />
        <FiltreCodes
          valeur={filtres.etat}
          codes={ETATS_LIGNE}
          domaine="lot_ligne_etat"
          titre="État"
          surChoix={(valeur) => changer({ etat: valeur })}
          largeur="w-36"
          testid="suivi-filtre-etat"
        />
        <FiltreCodes
          valeur={filtres.etape}
          codes={ETAPES_LOT}
          domaine="lot_etape"
          titre="Étape"
          surChoix={(valeur) => changer({ etape: valeur })}
          largeur="w-44"
          testid="suivi-filtre-etape"
        />
        <FiltreCodes
          valeur={filtres.retour}
          codes={RETOURS_LOT}
          domaine="lot_retour"
          titre="Retour"
          surChoix={(valeur) => changer({ retour: valeur })}
          largeur="w-40"
          testid="suivi-filtre-retour"
        />
        <FiltreCodes
          valeur={filtres.statutLot}
          codes={STATUTS_LOT}
          domaine="lot_statut"
          titre="État du lot"
          surChoix={(valeur) => changer({ statutLot: valeur })}
          largeur="w-44"
          testid="suivi-filtre-statut-lot"
        />
        {filtresActifs > 0 ? (
          <Button variant="ghost" size="sm" onClick={effacer} data-testid="suivi-effacer-filtres">
            <X className="h-4 w-4" aria-hidden="true" />
            Effacer les filtres
          </Button>
        ) : null}
      </div>

      {chargement && lignes.length === 0 ? (
        <LoadingState shape="table" />
      ) : erreur && lignes.length === 0 ? (
        <EmptyState
          variant="error"
          title="Le suivi n’a pas pu être chargé"
          description="Réessayez dans un instant."
          actionLabel="Réessayer"
          onAction={() => charger()}
        />
      ) : lignes.length === 0 ? (
        <EmptyState
          variant={filtresActifs ? 'filtered' : 'empty'}
          icon={ListChecks}
          title={filtresActifs ? 'Aucun résultat' : 'Aucun camion dans les lots'}
          description={filtresActifs ? 'Aucun camion ne correspond aux filtres actifs.' : undefined}
          aide={filtresActifs ? undefined : 'Les camions apparaissent ici dès qu’ils sont attribués à un lot.'}
        />
      ) : (
        <Card className="card-base">
          <CardContent className="p-0">
            <Table compact>
              <TableHeader>
                <TableRow>
                  <TableHead>Lot</TableHead>
                  <TableHead>Camion</TableHead>
                  <TableHead>Chauffeur</TableHead>
                  <TableHead className="text-right">Prévu</TableHead>
                  <TableHead className="text-right">Chargé</TableHead>
                  <TableHead>Étape</TableHead>
                  <TableHead className="hidden xl:table-cell">Dépôt</TableHead>
                  <TableHead>N° bon</TableHead>
                  <TableHead>
                    <EnteteDates />
                  </TableHead>
                  <TableHead>Trajet</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lignes.map((ligne) => (
                  <TableRow
                    key={ligne.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/lots/${ligne.lot_id}`)}
                    data-testid={`suivi-ligne-${ligne.id}`}
                  >
                    <TableCell>
                      <span className="inline-flex flex-col leading-tight">
                        <span className="whitespace-nowrap">
                          <LienLot lotId={ligne.lot_id} reference={ligne.lot_reference} />
                        </span>
                        {ligne.transporter_name ? (
                          <span className="whitespace-nowrap text-xs text-muted-foreground">{ligne.transporter_name}</span>
                        ) : null}
                      </span>
                    </TableCell>
                    <TableCell>
                      <CamionLigne ligne={ligne} />
                    </TableCell>
                    <TableCell>
                      <ChauffeurLigne ligne={ligne} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums">
                      {ligne.expected_quantity ? formatVolume(ligne.expected_quantity) : '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums">
                      {ligne.loaded_quantity ? formatVolume(ligne.loaded_quantity) : '—'}
                    </TableCell>
                    <TableCell>
                      <EtapeRetourLigne ligne={ligne} />
                    </TableCell>
                    <TableCell className="hidden whitespace-nowrap xl:table-cell">{ligne.loading_depot || '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">{ligne.loading_voucher_number || '—'}</TableCell>
                    <TableCell>
                      <DatesLigne ligne={ligne} />
                    </TableCell>
                    <TableCell>
                      <LienTrajet tripId={ligne.trip_id} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {lignes.length > 0 ? <BarrePagination pagination={pagination} nom="camions" /> : null}
    </div>
  );
};

export default LotsSuiviPage;
